/**
 * Local Browser Protocol Types
 *
 * WebSocket message protocol for communication between the CLI (LocalBrowserHost)
 * and the cloud API (RemoteBrowserClient). Enables cloud agents to control a
 * browser running on the user's local machine.
 */

// ============================================================================
// Session Types
// ============================================================================

export type LocalBrowserMode = 'playwright' | 'cdp';

export type LocalBrowserSessionStatus =
  | 'pending' // Session created, waiting for CLI to connect
  | 'connecting' // CLI is connecting
  | 'connected' // CLI connected, browser ready
  | 'running' // Agent is actively using browser
  | 'disconnected' // CLI disconnected
  | 'expired' // Session timed out
  | 'error'; // Error occurred

export type LocalBrowserSession = {
  id: string;
  orgId: string;
  createdBy: string | null;
  status: LocalBrowserSessionStatus;
  browserMode: LocalBrowserMode;
  instructions: string | null;
  startUrl: string | null;
  jobId: string | null;
  connectedAt: string | null;
  disconnectedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
};

// ============================================================================
// WebSocket Message Types
// ============================================================================

/**
 * Base message envelope for all WebSocket messages.
 */
interface BaseMessage {
  /** Unique message ID for request/response correlation */
  id: string;
  /** Unix timestamp in milliseconds */
  timestamp: number;
}

/**
 * Command sent from cloud API to CLI to execute a browser action.
 */
export interface BrowserCommand extends BaseMessage {
  type: 'command';
  /** Method name from IBrowserClient interface */
  method: string;
  /** Serialized method arguments */
  args: unknown[];
  /** Optional context ID for multi-context support. Omitted = default context. */
  contextId?: string;
}

/**
 * Response from CLI to cloud API with execution result.
 */
export interface BrowserResponse extends BaseMessage {
  type: 'response';
  /** ID of the original command */
  requestId: string;
  /** Whether the command executed successfully */
  success: boolean;
  /** Return value from the method (if success=true) */
  result?: unknown;
  /** Error message (if success=false) */
  error?: string;
  /** Error stack trace for debugging */
  stack?: string;
  /** Echoed context ID from the originating command */
  contextId?: string;
}

/**
 * Binary data message for large payloads (screenshots, traces).
 * Data is base64-encoded to work over JSON WebSocket.
 */
export interface BinaryDataMessage extends BaseMessage {
  type: 'binary';
  /** ID of the original command this data belongs to */
  requestId: string;
  /** Type of binary data */
  dataType: 'screenshot' | 'video' | 'trace';
  /** Base64-encoded binary data */
  data: string;
  /** MIME type of the data */
  mimeType: string;
}

/**
 * Heartbeat message for connection health monitoring.
 */
export interface HeartbeatMessage extends BaseMessage {
  type: 'heartbeat';
  /** Direction of the heartbeat */
  direction: 'ping' | 'pong';
}

/**
 * Session lifecycle event message.
 */
export interface SessionMessage extends BaseMessage {
  type: 'session';
  /** Type of session event */
  event:
    | 'connected' // CLI successfully connected
    | 'disconnected' // CLI disconnected
    | 'browser_ready' // Browser launched and ready
    | 'browser_closed' // Browser was closed
    | 'error'; // Session error occurred
  /** Browser mode being used */
  browserMode?: LocalBrowserMode;
  /** Error message if event='error' */
  error?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Union of all WebSocket message types.
 */
export type LocalBrowserMessage =
  | BrowserCommand
  | BrowserResponse
  | BinaryDataMessage
  | HeartbeatMessage
  | SessionMessage;

// ============================================================================
// Type Guards
// ============================================================================

export function isBrowserCommand(msg: unknown): msg is BrowserCommand {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    (msg as BrowserCommand).type === 'command' &&
    typeof (msg as BrowserCommand).method === 'string'
  );
}

export function isBrowserResponse(msg: unknown): msg is BrowserResponse {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    (msg as BrowserResponse).type === 'response' &&
    typeof (msg as BrowserResponse).requestId === 'string'
  );
}

export function isBinaryDataMessage(msg: unknown): msg is BinaryDataMessage {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    (msg as BinaryDataMessage).type === 'binary' &&
    typeof (msg as BinaryDataMessage).data === 'string'
  );
}

export function isHeartbeatMessage(msg: unknown): msg is HeartbeatMessage {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    (msg as HeartbeatMessage).type === 'heartbeat'
  );
}

export function isSessionMessage(msg: unknown): msg is SessionMessage {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    (msg as SessionMessage).type === 'session'
  );
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export type CreateLocalBrowserSessionRequest = {
  /** Browser mode: 'playwright' for fresh browser, 'cdp' for existing Chrome */
  browserMode?: LocalBrowserMode;
  /** Instructions for the cloud agent */
  instructions?: string | null;
  /** Initial URL to navigate to */
  startUrl?: string | null;
};

export type CreateLocalBrowserSessionResponse = {
  /** Session ID */
  sessionId: string;
  /** WebSocket token for CLI connection */
  wsToken: string;
  /** WebSocket URL for CLI to connect to */
  wsUrl: string;
  /** Session expiration time */
  expiresAt: string;
};

export type LocalBrowserSessionDetail = {
  session: LocalBrowserSession;
  createdBy: { id: string; name: string | null; email: string | null } | null;
};

export type ListLocalBrowserSessionsResponse = {
  items: LocalBrowserSessionDetail[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

// ============================================================================
// Job Types
// ============================================================================

export const LOCAL_BROWSER_JOB_TYPE = 'local-browser.run' as const;

export type LocalBrowserJobPayload = {
  sessionId: string;
  instructions: string;
  startUrl?: string | null;
};

// ============================================================================
// Constants
// ============================================================================

/** Default timeout for browser commands in milliseconds */
export const BROWSER_COMMAND_TIMEOUT_MS = 60_000;

/** Timeout for navigation commands in milliseconds */
export const BROWSER_NAVIGATION_TIMEOUT_MS = 120_000;

/** Timeout for screenshot commands in milliseconds */
export const BROWSER_SCREENSHOT_TIMEOUT_MS = 30_000;

/** Session expiration time in milliseconds (1 hour) */
export const SESSION_EXPIRATION_MS = 60 * 60 * 1000;

/** Heartbeat interval in milliseconds */
export const HEARTBEAT_INTERVAL_MS = 30_000;
